/**
 * The 'Header' part of a single Treebeard folder (i.e the folder icon and name, but not the toggle component)
 */

import React from "react";
import { observer } from "mobx-react";

import { IContainerProps } from "./TreebeardDecorators";
import { FolderComponent } from "./FolderComponent/FolderComponent";
import { Folder, OptionsVertical } from "../../icons";
import TextInput, { TextInputSize } from "../TextInput/TextInput";
import { OptionMenu, OptionMenuItem } from "../OptionMenu";
import IconButton from "../IconButton/IconButton";
import { ButtonSize } from "../Button/Button";
import { action, observable } from "mobx";
import { AnimationDuration } from "../../../styles/Animations";
import { CSSTransition } from "react-transition-group";
import OptionMenuSeparator from "../OptionMenu/OptionMenuSeparator";

@observer
export class TreebeardHeader extends React.Component<IContainerProps> {
  private inputElement: React.RefObject<HTMLInputElement>;

  @observable.ref
  private isRenaming = false;

  @observable.ref
  private newName: string | null = null;

  @observable.ref
  private isHovering: boolean = false;

  @observable.ref
  private isFocused: boolean = false;

  constructor(props: IContainerProps) {
    super(props);

    this.dragStartHandler = this.dragStartHandler.bind(this);
    this.dropHandler = this.dropHandler.bind(this);
    this.renameCancel = this.renameCancel.bind(this);
    this.renameConfirm = this.renameConfirm.bind(this);
    this.onChange = this.onChange.bind(this);
    this.keyPressed = this.keyPressed.bind(this);

    this.inputElement = React.createRef();
    this.isRenaming = props.node.renaming;
  }

  @action
  private startRenaming = () => {
    this.isRenaming = true;
  };

  private createNewFolder = () => {
    if (this.props.node?.onCreateFolder) {
      this.props.node.onCreateFolder(this.props.node.id);
    }
  };
  private deleteFolder = () => {
    if (this.props.node?.onDeleteFolder) {
      this.props.node.onDeleteFolder(this.props.node.id);
    }
  };

  @action
  private setIsHovering = (newValue: boolean): void => {
    this.isHovering = newValue;
  };

  @action
  private setIsFocused = (newValue: boolean): void => {
    this.isFocused = newValue;
  };

  public render(): JSX.Element {
    const {
      selected,
      name,
      toggled,
      children,
      id,
      disableSelect,
      isBeingDragged,
      draggable,
      onDragEnd,
      disableOptions,
    } = this.props.node;

    const hasSubfolders = children.length > 0;

    /**
     * @NOTE (som, 2020-03-18): on first glance, it seems strange that the `onDragStart` event is
     * handled here, wheras the <FolderComponent> handles all other HTML5 drag events.
     *
     * This is because <FolderComponent> is purely handling the hover behaviour when something is
     * dragged over it. There is no reason for it to know when it is being dragged.
     *
     * Also keep in mind that 'drag this folder' and 'drop something on this folder' are **entirely
     * separate concepts** from a low-level component point-of-view.
     */
    return (
      <div
        id={`tree-${id}-folder`}
        className="treebeard-folder"
        // `aria-expanded` should only be defined if this folder is expandable
        aria-expanded={hasSubfolders ? toggled : undefined}
        draggable={draggable}
        onDragStart={this.dragStartHandler}
        onDragEnd={onDragEnd}
        onMouseEnter={(): void => this.setIsHovering(true)}
        onMouseLeave={(): void => this.setIsHovering(false)}
        // We also need to add `onMouseOver` handle to make sure the hover state is
        // maintained when the mouse is on top of the card after closing the preview/edit modal
        onMouseOver={(): void => this.setIsHovering(true)}
        onFocus={(): void => this.setIsFocused(true)}
        onBlur={(): void => this.setIsFocused(false)}
      >
        <FolderComponent
          icon={<Folder />}
          as={this.isRenaming ? "div" : "button"}
          name={name}
          selected={selected}
          onClick={
            this.isRenaming
              ? undefined
              : (): void => {
                  if (this.props.onSelect) {
                    this.props.onSelect(this.props.node);
                  }
                }
          }
          onDrop={this.dropHandler}
          overrideNameComponent={this.isRenaming ? this.renderRenameInput() : undefined}
          disableSelect={disableSelect}
          disableDrop={isBeingDragged}
        />

        {!disableOptions && (
          <CSSTransition
            mountOnEnter
            unmountOnExit
            in={!this.isRenaming && (this.isHovering || this.isFocused)}
            timeout={AnimationDuration.Short}
            classNames="treebeard-folder__actions-"
          >
            <OptionMenu
              onClose={() => {
                this.isFocused = false;
              }}
              className={"treebeard-folder__action-menu"}
              size="small"
              button={
                <IconButton
                  aria-label="Folder options"
                  className="treebeard-folder__actions"
                  icon={<OptionsVertical />}
                  size={ButtonSize.Small}
                />
              }
            >
              <OptionMenuItem onClick={this.createNewFolder}>new folder</OptionMenuItem>
              <OptionMenuItem onClick={this.startRenaming}>rename folder</OptionMenuItem>
              <OptionMenuSeparator />
              <OptionMenuItem onClick={this.deleteFolder} destructive>
                delete folder
              </OptionMenuItem>
            </OptionMenu>
          </CSSTransition>
        )}
      </div>
    );
  }

  public componentDidMount(): void {
    // If we're rendering an <input> for renaming, automatically focus on the input
    if (!!this.inputElement.current) {
      this.inputElement.current.focus();
    }
  }

  public componentDidUpdate(): void {
    if (this.isRenaming) {
      setTimeout(() => {
        this.inputElement?.current?.focus();
      }, 300);
    }
  }

  private dragStartHandler(event: React.DragEvent): void {
    const { onDragStart, id } = this.props.node;

    onDragStart(event, id);
  }

  private dropHandler(event: React.DragEvent): void {
    const { onDropOnFolder, id } = this.props.node;
    if (!!onDropOnFolder) {
      onDropOnFolder(event, id);
    }
  }

  @action
  private renameCancel(): void {
    const { id, onRenameCancel } = this.props.node;

    if (!!onRenameCancel) {
      onRenameCancel(id);
    }

    this.resetRename();
  }

  @action
  private renameConfirm = (): void => {
    if (this.isRenaming) {
      const { id, onRenameConfirm } = this.props.node;

      if (!!this.newName) {
        if (!!onRenameConfirm) {
          onRenameConfirm(id, this.newName);
        }
      } else {
        // If we do any action that 'confirms' a rename while the folder name is empty, this will
        // instead be counted as a 'cancel'
        this.renameCancel();
      }

      this.resetRename();
    }
  };

  @action
  private resetRename = () => {
    this.isRenaming = false;
    this.isHovering = false;
    this.isFocused = false;
    this.newName = null;
  };

  private keyPressed(event: React.KeyboardEvent<HTMLElement>): void {
    switch (event.key) {
      case "Enter":
        this.renameConfirm();
        break;

      case "Escape":
        this.renameCancel();
        break;
    }
  }

  @action
  private onChange(value: string): void {
    this.newName = value;
    const { id, onNameChange } = this.props.node;

    if (!!onNameChange) {
      onNameChange(id, this.newName);
    }
  }

  /**
   * Render an input box for when this folder node is in rename mode
   */
  private renderRenameInput(): React.ReactElement {
    const { name, maxNameLength } = this.props.node;
    if (this.newName === null) {
      this.newName = name;
    }
    return (
      <TextInput
        labelHidden
        ref={this.inputElement}
        className="treebeard-header__name-input"
        label="Folder name"
        size={TextInputSize.Small}
        value={this.newName}
        onChange={this.onChange}
        onBlur={this.renameConfirm}
        onKeyDown={this.keyPressed}
        maxLength={maxNameLength}
        // We don't want clicks on  the input box to open/close the tree
        onClick={(e): void => {
          e.stopPropagation();
        }}
      />
    );
  }
}
